import { useEffect, useRef } from 'react'
import { ApiError } from '../api/client.ts'
import { type Refs, track, trackNow, type WaitWhat } from './events.ts'

/**
 * How long she waited for something, as she felt it: from the press to the answer, upload and OCR
 * and the model together (the server's own `draft_done.ms` is the model alone). A wait ends in
 * `done`, `failed`, or `left`: the page went away while it was still pending, which nothing on the
 * server can ever see. A wait that outlives its screen but finishes inside the app is still `done`.
 *
 * The browser owns the page lifecycle (ENGINEERING_RULES 22); everything pending is one set in this
 * module, closed out once whichever way it ends.
 */

/** What a wait was for: her activity, source, plan or word list as refs, and an exercise (a revise, a regenerate) in the data. */
export type WaitAbout = Refs & { exerciseId?: string }

type Pending = { what: WaitWhat; since: number; about?: WaitAbout }
const pending = new Set<Pending>()
let listening = false

const begin = (what: WaitWhat, about?: WaitAbout): Pending => {
	if (!listening && typeof window !== 'undefined') {
		listening = true
		// After the sink's own pagehide flush, so what is closed here is sent by `trackNow`.
		window.addEventListener('pagehide', () => {
			for (const p of [...pending]) end(p, 'left')
		})
	}
	const p = { what, since: Date.now(), about }
	pending.add(p)
	return p
}

function end(p: Pending, outcome: 'done' | 'failed' | 'left', status?: number): void {
	if (!pending.delete(p)) return
	const { exerciseId, ...refs } = p.about ?? {}
	const data = {
		what: p.what,
		ms: Math.min(Date.now() - p.since, 3_600_000),
		outcome,
		...(status !== undefined && { status }),
		...(exerciseId && { exerciseId }),
	}
	if (outcome === 'left') trackNow({ kind: 'wait', data }, refs)
	else track({ kind: 'wait', data }, refs)
}

/** The status a failure carries: the api's own, 0 for no answer at all (a dropped connection), none for anything else. */
const statusOf = (err: unknown): number | undefined =>
	err instanceof ApiError ? err.status : err instanceof TypeError ? 0 : undefined

/** Times a request she is waiting on and hands the same promise back, so a call site wraps and returns it. */
export function trackWait<T>(what: WaitWhat, promise: Promise<T>, about?: WaitAbout): Promise<T> {
	const p = begin(what, about)
	promise.then(
		() => end(p, 'done'),
		(err) => end(p, 'failed', statusOf(err)),
	)
	return promise
}

/**
 * The same for a wait a page already holds as state (a video being imported): true starts it,
 * false ends it (as `failed` when the page says it failed, else done), and a page that unmounts
 * while it is still true ends it as `left`.
 */
export function useWait(what: WaitWhat, waiting: boolean, failed = false, about?: WaitAbout): void {
	const open = useRef<Pending | null>(null)
	// Read when the wait begins; a later render with other ids does not restart or change it.
	const aboutNow = useRef(about)
	aboutNow.current = about
	useEffect(() => {
		if (waiting && !open.current) open.current = begin(what, aboutNow.current)
		if (!waiting && open.current) {
			end(open.current, failed ? 'failed' : 'done')
			open.current = null
		}
	}, [what, waiting, failed])
	useEffect(
		() => () => {
			if (open.current) end(open.current, 'left')
			open.current = null
		},
		[],
	)
}
